import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";

import OperatorInvoicesApi, {
  INVOICE_CATEGORIES,
} from "../../../../infrastructure/api/operatorInvoices";
import JalaliDatePicker from "../../shared/jalali-datepicker";
import { parseAmountInput, parseApiErrorMessage } from "./financialCoreUtils";

const emptyItem = () => ({
  title: "",
  description: "",
  quantity: "1",
  unitPrice: "",
  discountAmount: "0",
  taxAmount: "0",
});

const calcLine = (row) => {
  const qty = parseAmountInput(row.quantity) ?? 1;
  const unit = parseAmountInput(row.unitPrice) ?? 0;
  const discount = parseAmountInput(row.discountAmount) ?? 0;
  const tax = parseAmountInput(row.taxAmount) ?? 0;
  const base = qty * unit;
  return {
    base,
    discount,
    tax,
    total: Math.max(0, base - discount + tax),
  };
};

const OperatorInvoiceIssueDialog = ({
  open,
  onClose,
  operationsSource,
  requestInfoId,
  customerInfoId = 0,
  onCreated,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [form, setForm] = useState({
    invoiceCategory: 1,
    title: "",
    description: "",
    dueDate: "",
    sendSms: true,
    items: [emptyItem()],
  });

  const totals = useMemo(() => {
    let subtotal = 0;
    let discountTotal = 0;
    let taxTotal = 0;
    (form.items || []).forEach((row) => {
      const line = calcLine(row);
      subtotal += line.base;
      discountTotal += line.discount;
      taxTotal += line.tax;
    });
    return {
      subtotal,
      discountTotal,
      taxTotal,
      total: Math.max(0, subtotal - discountTotal + taxTotal),
    };
  }, [form.items]);

  const resetForm = () => {
    setForm({
      invoiceCategory: 1,
      title: "",
      description: "",
      dueDate: "",
      sendSms: true,
      items: [emptyItem()],
    });
    setCreatedResult(null);
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!form.title?.trim()) {
      toast.warning("عنوان فاکتور را وارد کنید.");
      return;
    }
    if (totals.total <= 0) {
      toast.warning("مبلغ کل فاکتور باید بزرگ‌تر از صفر باشد.");
      return;
    }

    const items = (form.items || [])
      .filter((r) => r.title?.trim())
      .map((r) => ({
        title: r.title.trim(),
        description: r.description || null,
        quantity: parseAmountInput(r.quantity) ?? 1,
        unitPrice: parseAmountInput(r.unitPrice) ?? 0,
        discountAmount: parseAmountInput(r.discountAmount) ?? 0,
        taxAmount: parseAmountInput(r.taxAmount) ?? 0,
      }));

    if (items.length === 0) {
      toast.warning("حداقل یک قلم فاکتور وارد کنید.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await OperatorInvoicesApi.create(operationsSource, {
        customerInfoId: Number(customerInfoId) || 0,
        requestInfoId: requestInfoId ? Number(requestInfoId) : null,
        invoiceCategory: Number(form.invoiceCategory),
        title: form.title.trim(),
        description: form.description || null,
        dueDate: form.dueDate || null,
        sendSmsNotification: form.sendSms,
        items,
      });

      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "صدور فاکتور ناموفق بود."));
        return;
      }

      setCreatedResult(res?.data ?? res?.Data ?? res);
      toast.success(res?.message || "فاکتور صادر شد");
      onCreated?.();
    } catch {
      toast.error("صدور فاکتور ناموفق بود.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendSms = async () => {
    const id = createdResult?.id ?? createdResult?.Id;
    if (!id) return;
    setSubmitting(true);
    try {
      const res = await OperatorInvoicesApi.sendSms(operationsSource, id);
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ارسال پیامک ناموفق بود."));
        return;
      }
      toast.success(res?.message || "پیامک ارسال شد");
      const detail = await OperatorInvoicesApi.getById(operationsSource, id);
      if (detail?.isSuccess !== false) {
        setCreatedResult(detail?.data ?? detail?.Data);
      }
    } catch {
      toast.error("ارسال پیامک ناموفق بود.");
    } finally {
      setSubmitting(false);
    }
  };

  const paymentLink =
    createdResult?.paymentLink ??
    createdResult?.PaymentLink ??
    (createdResult?.id || createdResult?.Id
      ? `${window.location.origin}/SLN/Customer/InvoicePayment/${
          createdResult.id ?? createdResult.Id
        }`
      : null);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>صدور فاکتور</DialogTitle>
      <DialogContent>
        {createdResult ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="success">فاکتور صادر شد</Alert>
            <Typography variant="body2">
              وضعیت پیامک:{" "}
              <strong>
                {createdResult.smsSendStatusTitle ??
                  createdResult.SmsSendStatusTitle ??
                  "—"}
              </strong>
            </Typography>
            {createdResult.smsErrorMessage || createdResult.SmsErrorMessage ? (
              <Alert severity="warning">
                {createdResult.smsErrorMessage ?? createdResult.SmsErrorMessage}
              </Alert>
            ) : null}
            <Typography variant="body2">
              شماره فاکتور:{" "}
              <strong>
                {createdResult.invoiceNumber ?? createdResult.InvoiceNumber}
              </strong>
            </Typography>
            <Typography variant="body2">
              مبلغ:{" "}
              <strong>
                {(createdResult.totalAmount ?? createdResult.TotalAmount ?? 0).toLocaleString(
                  "fa-IR"
                )}{" "}
                ریال
              </strong>
            </Typography>
            {paymentLink ? (
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                لینک پرداخت مشتری: {paymentLink}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="inv-cat-label">دسته فاکتور</InputLabel>
              <Select
                labelId="inv-cat-label"
                label="دسته فاکتور"
                value={form.invoiceCategory}
                onChange={(e) =>
                  setForm((f) => ({ ...f, invoiceCategory: e.target.value }))
                }
              >
                {INVOICE_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="عنوان فاکتور"
              size="small"
              fullWidth
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />

            <TextField
              label="توضیحات"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />

            <JalaliDatePicker
              label="سررسید"
              value={form.dueDate}
              onChange={(v) => setForm((f) => ({ ...f, dueDate: v }))}
            />

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>
                اقلام فاکتور
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setForm((f) => ({ ...f, items: [...(f.items || []), emptyItem()] }))
                }
              >
                افزودن قلم
              </Button>
            </Stack>

            {(form.items || []).map((row, idx) => (
              <Box
                key={idx}
                sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1.5 }}
              >
                <Stack spacing={1}>
                  <TextField
                    label="شرح"
                    size="small"
                    fullWidth
                    value={row.title}
                    onChange={(e) => {
                      const items = [...form.items];
                      items[idx] = { ...items[idx], title: e.target.value };
                      setForm((f) => ({ ...f, items }));
                    }}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      label="تعداد"
                      size="small"
                      sx={{ flex: 1 }}
                      value={row.quantity}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...items[idx], quantity: e.target.value };
                        setForm((f) => ({ ...f, items }));
                      }}
                    />
                    <TextField
                      label="مبلغ واحد (ریال)"
                      size="small"
                      sx={{ flex: 1 }}
                      value={row.unitPrice}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...items[idx], unitPrice: e.target.value };
                        setForm((f) => ({ ...f, items }));
                      }}
                    />
                    <TextField
                      label="تخفیف"
                      size="small"
                      sx={{ flex: 1 }}
                      value={row.discountAmount}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...items[idx], discountAmount: e.target.value };
                        setForm((f) => ({ ...f, items }));
                      }}
                    />
                    <TextField
                      label="مالیات"
                      size="small"
                      sx={{ flex: 1 }}
                      value={row.taxAmount}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...items[idx], taxAmount: e.target.value };
                        setForm((f) => ({ ...f, items }));
                      }}
                    />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      جمع قلم: {calcLine(row).total.toLocaleString("fa-IR")} ریال
                    </Typography>
                    {(form.items || []).length > 1 ? (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => {
                          const items = form.items.filter((_, i) => i !== idx);
                          setForm((f) => ({ ...f, items }));
                        }}
                      >
                        حذف
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Box>
            ))}

            <PaperTotals totals={totals} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.sendSms}
                  onChange={(e) => setForm((f) => ({ ...f, sendSms: e.target.checked }))}
                />
              }
              label="ارسال پیامک اطلاع‌رسانی پس از صدور"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {createdResult ? (
          <>
            <Button onClick={handleClose}>بستن</Button>
            <Button variant="outlined" onClick={handleResendSms} disabled={submitting}>
              ارسال مجدد پیامک
            </Button>
            {paymentLink ? (
              <Button
                variant="contained"
                component="a"
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                مشاهده فاکتور
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <Button onClick={handleClose}>انصراف</Button>
            <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
              {submitting ? "در حال صدور…" : "صدور فاکتور"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const PaperTotals = ({ totals }) => (
  <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1.5 }}>
    <Typography variant="caption" display="block">
      جمع اقلام: {totals.subtotal.toLocaleString("fa-IR")} ریال
    </Typography>
    <Typography variant="caption" display="block">
      تخفیف: {totals.discountTotal.toLocaleString("fa-IR")} ریال
    </Typography>
    <Typography variant="caption" display="block">
      مالیات: {totals.taxTotal.toLocaleString("fa-IR")} ریال
    </Typography>
    <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
      مبلغ کل: {totals.total.toLocaleString("fa-IR")} ریال
    </Typography>
  </Box>
);

PaperTotals.propTypes = {
  totals: PropTypes.object.isRequired,
};

OperatorInvoiceIssueDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  operationsSource: PropTypes.string.isRequired,
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  customerInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onCreated: PropTypes.func,
};

export default OperatorInvoiceIssueDialog;
